import { useMemo, useRef, useState } from 'react';

type Errors = {
    [key: string]: string;
};

type ValidationRule<T> = {
    required?: boolean | string;
    validate?: (value: T[keyof T], values: T) => string | null;
};

export type AppFormValidationSchema<T> = {
    [K in keyof T]?: ValidationRule<T>;
};

export type UseAppFormProps<T> = {
    values: T;
    errors: Errors;
    handleChange: (data: FormValues<T>) => void;
    validateForm: () => boolean;
    submit: () => void;
    reset: () => void;
    setApiErrors: (apiErrors: Partial<Record<keyof T, string[]>>) => void; // 👈 thêm
    formRef: React.RefObject<HTMLFormElement | null>;
    pristine: boolean;
    isError: boolean;
};

type FormValues<T> = {
    [K in keyof T]?: T[K];
};

const useAppForm = <T extends FormValues<T>>(
    initialValues: T,
    validationSchema: AppFormValidationSchema<T> = {}
): UseAppFormProps<T> => {
    const formRef = useRef<HTMLFormElement>(null);
    const [values, setValues] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Errors>({});

    const validateField = (name: keyof T, value: T[keyof T], allValues: T) => {
        const rules = validationSchema[name];
        if (!rules) return null;

        if (rules.required) {
            const message =
                typeof rules.required === 'string'
                    ? rules.required
                    : `${String(name)} is required`;

            const isEmpty =
                value === undefined ||
                value === null ||
                (typeof value === 'string' && value.trim() === '') ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === 'object' &&
                    !Array.isArray(value) &&
                    Object.keys(value).length === 0);

            if (isEmpty) return message;
        }

        if (rules.validate) {
            const error = rules.validate(value, allValues);
            if (error) return error;
        }

        return null;
    };

    const handleChange = (data: FormValues<T>) => {
        setValues((prev) => {
            const newValues = { ...prev, ...data };

            const fieldName = Object.keys(data)[0] as keyof T;
            const errorMsg = validateField(fieldName, newValues[fieldName], newValues);
            setErrors((prevErrors) => ({
                ...prevErrors,
                [fieldName]: errorMsg || '',
            }));

            return newValues;
        });
    };

    const validateForm = () => {
        const newErrors: Errors = {};
        (Object.keys(validationSchema) as (keyof T)[]).forEach((field) => {
            const error = validateField(field, values[field], values);
            if (error) {
                newErrors[String(field)] = error;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const submit = () => {
        if (formRef.current) {
            formRef.current.requestSubmit();
        }
    };

    const reset = () => {
        setValues(initialValues);
        setErrors({});
    };

    // 👇 thêm hàm để nhận lỗi từ API
    const setApiErrors = (apiErrors: Partial<Record<keyof T, string[]>>) => {
        const newErrors: Errors = {};
        for (const key in apiErrors) {
            if (apiErrors[key] && apiErrors[key]!.length > 0) {
                newErrors[key] = apiErrors[key]![0]; // lấy message đầu tiên
            }
        }
        setErrors(newErrors);
    };

    const pristine = useMemo(() => {
        return JSON.stringify(values) === JSON.stringify(initialValues);
    }, [values, initialValues]);

    const isError = useMemo(() => {
        const newErrors: Errors = {};
        (Object.keys(validationSchema) as (keyof T)[]).forEach((field) => {
            const error = validateField(field, values[field], values);
            if (error) {
                newErrors[String(field)] = error;
            }
        });
        return Object.keys(newErrors).length > 0;
    }, [values])
    return {
        formRef,
        values,
        errors,
        pristine,
        isError,
        handleChange,
        validateForm,
        submit,
        reset,
        setApiErrors, // 👈 expose ra ngoài
    };
};

export default useAppForm;
